/**
 * /tools/:toolKey/view?clientId=X — NL-024 (2026-06-09).
 *
 * Generic read-only renderer for any completed tool. Fetches the latest
 * `tool_completions` row via `getToolCompletion`; if found + completed,
 * renders the payload as a brand-styled labelled list. Otherwise shows
 * a "not yet finalized" empty state pointing the client back to the
 * editable form.
 *
 * Per-tool payload structure varies wildly. The renderer walks the JSON
 * generically — primitive → line; array of primitives → bullet list;
 * array of objects → grouped block; nested object → indented section.
 * Per-tool bespoke renderers can replace this view later without
 * changing the route.
 *
 * Wired (NL carry-forward (c)): `useToolRouteEffects` (mounted in AppLayout)
 * redirects any editable `/tools/:key` to this `/view` once the completion is
 * finalized — unless a `?coachEdit` coach-edit session is active. See
 * `lib/use-tool-route-effects.ts` + `lib/tool-routes.ts`.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { useAuth } from '@/app/auth-context';
import * as api from '@/lib/api-client';
import { useClientId } from '@/lib/use-client-id';
import { PrintButton } from '@/components/print-button';

export function FinishedToolView(): JSX.Element {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  const clientId = useClientId();
  const { toolKey } = useParams<{ toolKey: string }>();

  const [completion, setCompletion] = useState<api.ToolCompletion | null>(null);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    let active = true;
    if (!clientId || !toolKey) {
      setHydrated(true);
      return;
    }
    void (async () => {
      try {
        const result = await api.getToolCompletion(accessToken, clientId, toolKey);
        if (!active) return;
        if (result && result.status === 'completed') {
          setCompletion(result);
        } else {
          setCompletion(null);
        }
      } catch {
        if (active) setCompletion(null);
      } finally {
        if (active) setHydrated(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, clientId, toolKey]);

  if (!clientId) {
    return (
      <section
        className="max-w-2xl mx-auto px-s5 py-s8"
        data-testid="finished-tool-view-missing-client"
      >
        <h1 className="font-display text-display-lg text-ink">
          {t('tools.finishedView.title')}
        </h1>
        <p className="mt-s3 text-flame-ember">
          {t('tools.finishedView.errors.missingClient')}
        </p>
      </section>
    );
  }

  if (!toolKey) {
    return (
      <section
        className="max-w-2xl mx-auto px-s5 py-s8"
        data-testid="finished-tool-view-missing-key"
      >
        <h1 className="font-display text-display-lg text-ink">
          {t('tools.finishedView.title')}
        </h1>
        <p className="mt-s3 text-flame-ember">
          {t('tools.finishedView.errors.missingKey')}
        </p>
      </section>
    );
  }

  if (!hydrated) {
    return (
      <section
        className="max-w-3xl mx-auto px-s5 py-s7"
        data-testid="finished-tool-view-loading"
      >
        <p className="text-sm text-ink/70">{t('tools.finishedView.loading')}</p>
      </section>
    );
  }

  if (!completion) {
    return (
      <section
        className="max-w-3xl mx-auto px-s5 py-s7"
        data-testid="finished-tool-view-not-finalized"
      >
        <header className="mb-s6">
          <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
            {t('tools.finishedView.eyebrow')}
          </p>
          <h1 className="font-display text-display-xl text-ink mt-s2">
            {t('tools.finishedView.title')}
          </h1>
        </header>
        <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
          <h2 className="font-display text-display-sm text-ink">
            {t('tools.finishedView.notFinalized.title')}
          </h2>
          <p className="mt-s2 text-gray-2">
            {t('tools.finishedView.notFinalized.body')}
          </p>
          <Link
            to={`/tools/${encodeURIComponent(toolKey)}?clientId=${encodeURIComponent(clientId)}`}
            className="btn-primary inline-flex mt-s4"
            data-testid="finished-tool-view-open-editable"
          >
            {t('tools.finishedView.notFinalized.ctaLabel')}
          </Link>
        </article>
      </section>
    );
  }

  return (
    <section
      className="max-w-3xl mx-auto px-s5 py-s7"
      data-testid="finished-tool-view"
    >
      <div className="flex justify-end mb-s2">
        <PrintButton />
      </div>
      <header className="mb-s6">
        <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
          {t('tools.finishedView.eyebrow')}
        </p>
        <h1 className="font-display text-display-xl text-ink mt-s2">
          {humanizeToolKey(toolKey)}
        </h1>
        {completion.completed_at && (
          <p className="mt-s2 text-sm text-gray-2">
            {t('tools.finishedView.completedAt', {
              when: completion.completed_at.slice(0, 19).replace('T', ' '),
            })}
          </p>
        )}
      </header>

      <article
        className="rounded-lg border border-bone-3 bg-bone-1 p-s5 space-y-s3"
        data-testid="finished-tool-view-payload"
      >
        <PayloadRenderer payload={completion.payload} />
      </article>

      <p
        className="mt-s5 text-sm text-gray-2 italic"
        data-testid="finished-tool-view-ask-coach"
      >
        {t('tools.finishedView.askCoach')}
      </p>
    </section>
  );
}

/* ─────────────────────────────  Renderer  ───────────────────────────── */

/**
 * Walk an arbitrary JSON payload and render it as a readable nested
 * list. Optimized for clarity, not for per-tool polish. Per-tool
 * bespoke views can replace this without changing the route.
 */
function PayloadRenderer({ payload }: { payload: unknown }): JSX.Element {
  if (payload === null || payload === undefined) {
    return <p className="text-gray-2 italic">—</p>;
  }
  return <RenderValue value={payload} depth={0} />;
}

function RenderValue({ value, depth }: { value: unknown; depth: number }): JSX.Element {
  if (value === null || value === undefined) {
    return <span className="text-gray-2 italic">—</span>;
  }
  if (typeof value === 'string') {
    if (value.length === 0) return <span className="text-gray-2 italic">—</span>;
    return <span className="text-ink whitespace-pre-wrap">{value}</span>;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return <span className="text-ink font-mono">{String(value)}</span>;
  }
  if (Array.isArray(value)) {
    const arr = value as unknown[];
    if (arr.length === 0) return <span className="text-gray-2 italic">—</span>;
    return (
      <ul className="list-disc list-inside space-y-1 text-ink">
        {arr.map((v, i) => (
          <li key={i}>
            <RenderValue value={v} depth={depth + 1} />
          </li>
        ))}
      </ul>
    );
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return <span className="text-gray-2 italic">—</span>;
    return (
      <dl
        className={
          depth === 0 ? 'space-y-s3' : 'space-y-s2 pl-s4 border-l border-bone-3'
        }
      >
        {entries.map(([k, v]) => (
          <div key={k}>
            <dt className="text-xs font-mono uppercase tracking-wide text-gray-2">
              {humanizeKey(k)}
            </dt>
            <dd className="mt-s1 text-ink">
              <RenderValue value={v} depth={depth + 1} />
            </dd>
          </div>
        ))}
      </dl>
    );
  }
  return <span className="text-gray-2 italic">{String(value)}</span>;
}

/* ─────────────────────────────  Helpers  ───────────────────────────── */

/** virtue-affirmations → "Virtue Affirmations" */
function humanizeToolKey(key: string): string {
  return key
    .split(/[-_]/)
    .filter((p) => p.length > 0)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ');
}

/** virtueAffirmations / virtue_affirmations → "Virtue affirmations" */
function humanizeKey(key: string): string {
  const spaced = key
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();
  if (spaced.length === 0) return key;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
